import Vue from 'vue';
import { transformStyle } from '@tencent/magic-ui-util';
import { renderSvelteComponent, isSvelteComponent } from '@/svelte-comp-adapter';
import scrollMixin from './mixins/scroll';
import { spliceNotRenderComponent } from './mixins/renderView';

export default {
  name: 'magic-ui-component',

  mixins: [scrollMixin],

  provide() {
    return {
      hoc: this,
    };
  },

  inject: {
    app: {
      default: {},
    },
  },

  props: {
    config: {
      type: [Object],
    },

    model: {
      type: [Array, Object],
    },
  },

  data() {
    return {
      visible: true,
      disabled: false,
      data: {},
      focused: false,
    };
  },

  watch: {
    config: {
      handler(value) {
        // 不同于 vue 组件，可以直接通过 props 实现响应式更新
        // svelte 组件需要手动调用更新
        if (this.isSvelte && this.svelteHelper) {
          let style = JSON.parse(JSON.stringify(value.style || {}));
          if (value.style) {
            let disabledStyle = JSON.parse(JSON.stringify(value.disabledStyle || {}));
            style = this.getStyle(style);
            if (this.disabled && disabledStyle) {
              disabledStyle = this.getStyle(disabledStyle);

              // 过滤置灰场景下没有填写的样式，默认使用正常情况下的相同配置
              disabledStyle = Object.keys(disabledStyle).reduce((obj, key) => {
                if (disabledStyle[key]) {
                  return {
                    ...obj,
                    [key]: disabledStyle[key],
                  };
                }
                return { ...obj };
              }, {});

              style = {
                ...style,
                ...disabledStyle,
              };
            }
            if (!this.visible) {
              style.display = 'none';
            }
          }
          this.svelteHelper.updateComponentStyle?.(style);
        }
      },
      deep: true,
    },
  },

  computed: {
    enableFocus() {
      return this.config?.tvfocusConfig?.enable && this.inHippy;
    },

    tagName() {
      const { type } = this.config;

      let tagName = `magic-ui-${type.replace(/([A-Z])/g, '-$1').toLowerCase()}`;

      if (type === 'action-button') {
        const act = (this.config.act || '').replace(/([A-Z])/g, '-$1').toLowerCase();
        tagName = 'magic-ui-button';
        if (act === 'url') {
          tagName = 'magic-ui-link';
        } else if (act && !act.endsWith('pop')) {
          tagName = `magic-ui-${act}-button`;
        }
      }
      return tagName;
    },

    isSvelte() {
      return isSvelteComponent(this.tagName);
    },

    className() {
      if (!this.config) {
        return '';
      }

      let { className } = this.config;
      if (typeof className === 'function') {
        className = className(this, { model: this.model });
      }
      if (Array.isArray(className)) {
        className = [...className];
      } else if (typeof className === 'string') {
        className = [className];
      } else {
        className = [];
      }

      if (this.disabled) {
        className.push('disabled');
      }

      if (!this.inMagic && this.scrollHide) {
        className.push('magic-scroll-hide');
      }

      if (this.enableFocus) {
        className.push('focusable');
      }

      return className.join(' ');
    },

    style() {
      if (!this.config) {
        return '';
      }

      let { style, disabledStyle } = this.config;

      style = this.getStyle(style || {});

      if (this.disabled && disabledStyle) {
        disabledStyle = this.getStyle(disabledStyle);

        // 过滤置灰场景下没有填写的样式，默认使用正常情况下的相同配置
        disabledStyle = Object.keys(disabledStyle).reduce((obj, key) => {
          if (disabledStyle[key]) {
            return {
              ...obj,
              [key]: disabledStyle[key],
            };
          }
          return { ...obj };
        }, {});

        style = {
          ...style,
          ...disabledStyle,
        };
      }

      if (!this.visible) {
        style.display = 'none';
      }

      return style;
    },

    focusStyle() {
      if (!this.focused || !this.enableFocus) {
        return this.style;
      }

      const { pageConfig } = this.app;
      const { tvfocusConfig } = this.config;

      let style = pageConfig.tvfocusConfig.style || {};

      if (tvfocusConfig?.type === 'custom') {
        style = {
          ...style,
          ...tvfocusConfig.style,
        };
      }

      return {
        ...this.style,
        ...this.getStyle(style),
      };
    },
  },

  methods: {
    show() {
      this.visible = true;
    },

    hide() {
      this.visible = false;
    },

    display() {
      const fuc = this.config.display;
      if (typeof fuc === 'function') {
        return fuc.bind(this)(this, { model: this.model });
      }

      if (fuc === false) {
        return fuc;
      }

      return true;
    },

    getReportDirectives() {
      const directives = [];

      if (this.inMagic) {
        return directives;
      }

      const report = this.config.report || {};

      if (report.module) {
        report.module.comp_type = this.config.type;
        directives.push({
          name: 'report',
          rawName: 'v-report.module',
          value: report.module,
          expression: 'report.module',
          modifiers: { module: true },
        });
      }

      if (report.click) {
        report.click.comp_type = this.config.type;
        if (this.config.modId) {
          report.click.f_magic_modid = this.config.modId;
        }
        directives.push({
          name: 'report',
          rawName: 'v-report.click',
          value: report.click,
          expression: 'report.click',
          modifiers: { click: true },
        });
      }

      if (report.expose) {
        report.expose.comp_type = this.config.type;
        if (this.config.modId) {
          report.expose.f_magic_modid = this.config.modId;
        }
        directives.push({
          name: 'report',
          rawName: 'v-report.expose',
          value: report.expose,
          expression: 'report.expose',
          modifiers: { expose: true },
        });
      }

      return directives;
    },

    getStyle(config) {
      let style = config;
      if (typeof config === 'function') {
        style = config.bind(this)(this, { model: this.model });
      }

      const base = ['Hippy-TV', 'TV'].includes(this.app.pageConfig.pageType) ? 1920 : 375;
      return transformStyle(style, Vue, this.inHippy, base);
    },

    getComponentOptions() {
      return {
        props: {
          model: this.model,
          config: this.config,
          disabled: this.disabled,
        },
        ref: 'magicComp',
        directives: this.getReportDirectives(),
      };
    },

    renderFocusable(h) {
      const { id } = this.config;
      const { pageConfig } = this.app;

      const {
        backgroundImage = '',
        backgroundSize = '100%',
        backgroundRepeat = 'no-repeat',
        ...style
      } = this.focusStyle;

      const children = [h(this.tagName, {
        ...this.getComponentOptions(),
        style: {
          backgroundImage,
          backgroundSize,
          backgroundRepeat,
        },
      })];

      const focusableOptions = {
        ref: 'focusable',

        style: {
          ...style,
          // overflow: 'hidden',
        },

        props: {
          staticClass: this.className,
          tvRequestFocus: `${pageConfig.tvfocusConfig?.defaultFocus}` === `${id}`,
        },

        on: {
          'tvfocus-focus': () => {
            this.focused = true;
          },
          'tvfocus-blur': () => {
            this.focused = false;
          },
        },

        nativeOn: {
          click: (event) => {
            const { magicComp } = this.$refs;
            // eslint-disable-next-line no-param-reassign
            event.bubbles = false;

            if (magicComp.$el) {
              magicComp.$el.dispatchEvent(event);
            }
          },
        },
      };

      return h(
        'focusable',
        focusableOptions,
        children,
      );
    },
  },

  render(h) {
    const display = this.display();
    if (!spliceNotRenderComponent(this, display)) {
      return '';
    }

    if (!display) {
      return '';
    }

    const { id, type } = this.config;

    if (this.enableFocus) {
      return this.renderFocusable(h);
    }

    const componentOptions = {
      ...this.getComponentOptions(),
      class: this.className,
      style: this.style,
      attrs: { id },
    };

    this.start = new Date().getTime();
    console.log('[ui] render:', this.tagName, this.config.id, this.start);

    // 如果是 svelte 组件，需要先提供一个包装 svelte 组件的容器
    if (this.isSvelte) {
      this.componentOptions = componentOptions;
      return h('div', {
        // 标记为 svelte 组件
        attrs: {
          'data-svelte': type,
        },
      });
    }

    return h(this.tagName, componentOptions);
  },

  mounted() {
    const { type: compType, id: compId } = this.config;

    console.log('[ui] mounted: ', this.tagName, this.config.id, (new Date().getTime()) - this.start);
    window.reportAegisTime?.({
      name: this.isSvelte ? 'svelte:mounted' : 'vue:mounted',
      ext1: compType,
      ext2: compId,
      ext3: window.actId,
    });

    if (this.isSvelte) {
      const renderSvelte = () => {
        console.log('[ui] 渲染 svelte 组件 ', compType);
        this.svelteHelper = renderSvelteComponent({
          props: {
            target: this.$el,
            config: this.config,
            model: this.model,
            componentOptions: this.componentOptions,
          },
        });
      };

      if (window.mappingInfo) {
        renderSvelte();
      } else {
        // editor 端需要等到 mappingInfoReady 之后再调用
        this.$bus.$on('windowMappingInfoReady', renderSvelte);
      }
    }
  },
};
